/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */


import {ITokenProvider, ITokenResponse} from "@fluidframework/routerlicious-driver";
import {isExpiredToken} from "../utils/tokenUtils";
import {meetingPortalAuthenticatorClient} from "../../MeetingPortalAuthenticatorClient";

/**
 * Default token provider in case the host does not provide one. It simply caches the provided jwt and returns it back.
 */

export class MeetingPortalTokenProvider implements ITokenProvider {
    private fluidToken: string = '';

    constructor(
        private readonly jwt: string,
        private readonly joinMeetingId: string,
        private readonly joinMeetingPasscode: string,
        private readonly user: { id: string; name: string; },
        private readonly communicationsIdentifier: string | null,
    ) {
        this.fluidToken = jwt;
    }

    public async fetchOrdererToken(tenantId: string, documentId?: string, refresh?: boolean): Promise<ITokenResponse> {
        return this.fetchStorageToken(tenantId, documentId, refresh);
    }

    public async fetchStorageToken(_tenantId: string, _documentId?: string, refresh?: boolean): Promise<ITokenResponse> {
        return this.getToken(refresh);
    }

    private async getToken(refresh?: boolean): Promise<ITokenResponse> {
        if (!isExpiredToken(this.fluidToken) && !refresh) {
            return {
                fromCache: true,
                jwt: this.fluidToken,
            };
        }

        try {
            const response = await meetingPortalAuthenticatorClient.getMeetingDetails(
                this.joinMeetingId,
                this.joinMeetingPasscode,
                this.user,
                this.communicationsIdentifier,
            );
            this.fluidToken = response.fluidToken;
        } catch (error) {
            console.error('Could not fetch meeting details', (error as Error).message);
        }

        return {
            fromCache: false,
            jwt: this.fluidToken,
        };
    }
}